// ComplianceTab.js — Compliance Fulfilment pillar home
// Onboarding stepper, KYC/CDS status, per-market requirements, documents, consents

import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, CheckCircle, Clock, AlertTriangle, FileText, Building2,
  ChevronRight, ClipboardCheck, ExternalLink
} from 'lucide-react';
import { ONBOARDING_STEPS, ONBOARDING_PROGRESS } from '../../data/dealingMockData';
import { mockMarketOnboardings } from '../../hooks/useKycMockData';

// ─── Demo data ──────────────────────────────────────────────────────────

const DEMO_KYC_STATUS = [
  { bank: 'Horizon Capital Bank', market: 'Kenya', kycStatus: 'approved', cdsAccount: 'CSD-KE-001', cdsStatus: 'active', lastReview: '2026-04-15', nextReview: '2027-04-15' },
  { bank: 'Meridian Trust Bank', market: 'Kenya', kycStatus: 'approved', cdsAccount: 'CSD-KE-002', cdsStatus: 'active', lastReview: '2026-06-20', nextReview: '2027-06-20' },
  { bank: 'Atlas Development Bank', market: 'Uganda', kycStatus: 'in_progress', cdsAccount: 'CSD-UG-001', cdsStatus: 'pending', lastReview: null, nextReview: null },
  { bank: 'Pioneer Exchange', market: 'Tanzania', kycStatus: 'pending', cdsAccount: null, cdsStatus: 'not_started', lastReview: null, nextReview: null },
];

const DEMO_DOCUMENTS = [
  { id: 'DOC-001', name: 'Certificate of Incorporation', type: 'identity', uploadedAt: '2026-03-15', status: 'verified' },
  { id: 'DOC-002', name: 'Board Resolution', type: 'authority', uploadedAt: '2026-03-15', status: 'verified' },
  { id: 'DOC-003', name: 'Passport — J. Mwangi (Director)', type: 'identity', uploadedAt: '2026-03-16', status: 'verified' },
  { id: 'DOC-004', name: 'KRA PIN Certificate', type: 'tax', uploadedAt: '2026-03-16', status: 'verified' },
  { id: 'DOC-005', name: 'Bank Statement (3 months)', type: 'financial', uploadedAt: '2026-06-01', status: 'verified' },
  { id: 'DOC-006', name: 'Proof of Address', type: 'identity', uploadedAt: '2026-06-01', status: 'pending_review' },
];

const DEMO_CONSENTS = [
  { id: 'CON-001', label: 'Terms & Conditions', grantedAt: '2026-03-15', version: '2.1' },
  { id: 'CON-002', label: 'Privacy Policy', grantedAt: '2026-03-15', version: '1.4' },
  { id: 'CON-003', label: 'Electronic Communications', grantedAt: '2026-03-15', version: '1.0' },
  { id: 'CON-004', label: 'Cross-border Data Transfer', grantedAt: '2026-03-15', version: '1.1' },
];

const statusIcon = (status) => {
  if (status === 'completed' || status === 'approved' || status === 'verified' || status === 'active') {
    return <CheckCircle className="w-4 h-4 text-emerald-500" />;
  }
  if (status === 'in_progress' || status === 'pending' || status === 'pending_review') {
    return <Clock className="w-4 h-4 text-amber-500" />;
  }
  if (status === 'blocked') {
    return <AlertTriangle className="w-4 h-4 text-red-500" />;
  }
  return <Clock className="w-4 h-4 text-zinc-300" />;
};

const statusLabel = (status) => {
  const labels = {
    completed: 'Completed', approved: 'Approved', verified: 'Verified', active: 'Active',
    in_progress: 'In Progress', pending: 'Pending', pending_review: 'Under Review',
    blocked: 'Blocked', not_started: 'Not Started',
  };
  return labels[status] || status;
};

const statusBadgeClass = (status) => {
  if (status === 'completed' || status === 'approved' || status === 'verified' || status === 'active') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (status === 'in_progress' || status === 'pending' || status === 'pending_review') {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }
  if (status === 'blocked') {
    return 'bg-red-50 text-red-700 border-red-200';
  }
  return 'bg-zinc-50 text-zinc-500 border-zinc-200';
};

const formatDate = (d) => {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Component ──────────────────────────────────────────────────────────

const ComplianceTab = ({ role }) => {
  const isFamilyOffice = role === 'investor_family_office' || role === 'investor_admin';
  // Use first CSD account for onboarding progress (FO sees all, individual sees CSD-KE-003)
  const progressKey = isFamilyOffice ? 'CSD-KE-001' : 'CSD-KE-003';
  const progress = ONBOARDING_PROGRESS[progressKey] || {};

  // Market requirements — use investor-facing subset from mock
  const [marketReqs, setMarketReqs] = useState([]);
  useEffect(() => {
    // In production this would call kycApi.getMarketRules()
    // For now use mockMarketOnboardings filtered to first few entries
    const subset = mockMarketOnboardings.slice(0, isFamilyOffice ? 3 : 1);
    setMarketReqs(subset);
  }, [isFamilyOffice]);

  const completedSteps = ONBOARDING_STEPS.filter(s => progress[s.key] === 'completed').length;
  const totalSteps = ONBOARDING_STEPS.length;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck className="w-4 h-4 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Onboarding</span>
          </div>
          <p className="font-mono text-xl font-bold text-black">{completedSteps}/{totalSteps}</p>
          <p className="font-mono text-[10px] text-zinc-400">steps complete</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">KYC Status</span>
          </div>
          <p className="font-mono text-xl font-bold text-black">{DEMO_KYC_STATUS.filter(b => b.kycStatus === 'approved').length}/{DEMO_KYC_STATUS.length}</p>
          <p className="font-mono text-[10px] text-zinc-400">banks approved</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Documents</span>
          </div>
          <p className="font-mono text-xl font-bold text-black">{DEMO_DOCUMENTS.filter(d => d.status === 'verified').length}/{DEMO_DOCUMENTS.length}</p>
          <p className="font-mono text-[10px] text-zinc-400">verified</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Markets</span>
          </div>
          <p className="font-mono text-xl font-bold text-black">{marketReqs.filter(m => m.status === 'completed').length}/{marketReqs.length}</p>
          <p className="font-mono text-[10px] text-zinc-400">markets onboarded</p>
        </div>
      </div>

      {/* Onboarding Stepper */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Onboarding Progress</h3>
        <div className="space-y-3">
          {ONBOARDING_STEPS.map((step, i) => {
            const status = progress[step.key] || 'pending';
            return (
              <div key={step.key} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  {statusIcon(status)}
                  {i < ONBOARDING_STEPS.length - 1 && (
                    <div className={`w-px h-6 mt-1 ${status === 'completed' ? 'bg-emerald-300' : 'bg-zinc-200'}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-zinc-800">{step.label}</span>
                    <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase border rounded-none ${statusBadgeClass(status)}`}>
                      {statusLabel(status)}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{step.description}</p>
                </div>
                <span className="font-mono text-[9px] uppercase text-zinc-400 shrink-0">{step.owner}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* KYC & CDS Status per Bank */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">KYC & CDS Status</h3>
          {isFamilyOffice && (
            <a
              href="/investor/admin/kyc"
              className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500 hover:text-zinc-700 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              KYC Cases
            </a>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Bank</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Market</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">KYC</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">CDS Account</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">CDS Status</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Next Review</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_KYC_STATUS.map((row, i) => (
                <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="py-2 px-3 font-mono text-xs font-bold">{row.bank}</td>
                  <td className="py-2 px-3 text-xs text-zinc-600">{row.market}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase border rounded-none ${statusBadgeClass(row.kycStatus)}`}>
                      {statusIcon(row.kycStatus)}
                      {statusLabel(row.kycStatus)}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{row.cdsAccount || '\u2014'}</td>
                  <td className="py-2 px-3">
                    <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase border rounded-none ${statusBadgeClass(row.cdsStatus)}`}>
                      {statusLabel(row.cdsStatus)}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-zinc-500">{formatDate(row.nextReview)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-Market Requirements */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Market Requirements</h3>
        {marketReqs.length === 0 ? (
          <p className="text-sm text-zinc-400">No market onboarding records found.</p>
        ) : (
          <div className="space-y-4">
            {marketReqs.map((mkt) => (
              <div key={mkt.id} className="border border-zinc-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold">{mkt.market_name}</span>
                    <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase border rounded-none ${statusBadgeClass(mkt.status)}`}>
                      {statusLabel(mkt.status)}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-zinc-400">{mkt.completed_requirements}/{mkt.total_requirements} requirements</span>
                </div>
                {/* Progress bar */}
                <div className="w-full h-1.5 bg-zinc-100 mb-3">
                  <div
                    className="h-full bg-zinc-700 transition-all"
                    style={{ width: `${(mkt.completed_requirements / mkt.total_requirements) * 100}%` }}
                  />
                </div>
                <div className="text-[10px] text-zinc-400 font-mono">
                  Regulator: {mkt.regulator} &middot; CSD: {mkt.csd_name}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Document Vault</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Document</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Type</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Uploaded</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_DOCUMENTS.map((doc) => (
                <tr key={doc.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="py-2 px-3 font-mono text-xs font-bold">{doc.name}</td>
                  <td className="py-2 px-3 font-mono text-[10px] uppercase text-zinc-500">{doc.type}</td>
                  <td className="py-2 px-3 text-xs text-zinc-500">{formatDate(doc.uploadedAt)}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase border rounded-none ${statusBadgeClass(doc.status)}`}>
                      {statusIcon(doc.status)}
                      {statusLabel(doc.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Consents */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Consents</h3>
        <div className="space-y-2">
          {DEMO_CONSENTS.map((consent) => (
            <div key={consent.id} className="flex items-center justify-between py-2 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-xs text-zinc-700">{consent.label}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] text-zinc-400">v{consent.version}</span>
                <span className="font-mono text-[10px] text-zinc-400">{formatDate(consent.grantedAt)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceTab;
